import { createDocumentState } from './document-state.js';
import { createComponentErrorEvent } from './error-event.js';
import { isCommittedComponentNode } from './vnode.js';
import { findNextSiblingDomAnchor, renderComponent, setActiveSchedulerUpdateParents, } from './reconcile.js';
import { defaultStyleManager } from './diff-props.js';
const CASCADING_UPDATE_WARN_THRESHOLD = 50;
const MAX_CASCADING_COMPONENT_UPDATES = 50;
function getComponentName(vnode) {
    return vnode.type.name || 'Anonymous';
}
function formatComponentCounts(counts) {
    return Array.from(counts, ([name, count]) => `${name} x${count}`).join(', ');
}
/**
 * Creates the DOM update scheduler used by the component runtime.
 *
 * @param doc Document associated with the rendered tree.
 * @param rootTarget Event target that receives runtime errors.
 * @param styles Style manager used during reconciliation.
 * @returns A scheduler instance.
 */
export function createScheduler(doc, rootTarget, styles = defaultStyleManager) {
    let documentState = createDocumentState(doc);
    let scheduled = new Map();
    let workTasks = [];
    let commitPhaseTasks = [];
    let postCommitTasks = [];
    let flushScheduled = false;
    let flushing = false;
    let cascadingComponentUpdateCount = 0;
    let cascadingComponentUpdateCounts = new WeakMap();
    let cascadingComponentNameCounts = new Map();
    let warnedAboutCascadingUpdates = false;
    let resetScheduled = false;
    let phaseEvents = new EventTarget();
    let phaseListenerCounts = {
        beforeUpdate: 0,
        commit: 0,
    };
    let scheduler;
    function dispatchError(error) {
        console.error(error);
        rootTarget.dispatchEvent(createComponentErrorEvent(error));
    }
    function scheduleCounterReset() {
        if (resetScheduled)
            return;
        resetScheduled = true;
        // Reset when control returns to the event loop while still allowing
        // microtask-driven flushes in the same turn to count as cascading.
        setTimeout(() => {
            cascadingComponentUpdateCount = 0;
            cascadingComponentUpdateCounts = new WeakMap();
            cascadingComponentNameCounts = new Map();
            warnedAboutCascadingUpdates = false;
            resetScheduled = false;
        }, 0);
    }
    function trackCascadingUpdate(vnode) {
        cascadingComponentUpdateCount++;
        let componentName = getComponentName(vnode);
        cascadingComponentNameCounts.set(componentName, (cascadingComponentNameCounts.get(componentName) ?? 0) + 1);
        let componentUpdateCount = (cascadingComponentUpdateCounts.get(vnode) ?? 0) + 1;
        cascadingComponentUpdateCounts.set(vnode, componentUpdateCount);
        scheduleCounterReset();
        if (!warnedAboutCascadingUpdates &&
            cascadingComponentUpdateCount >= CASCADING_UPDATE_WARN_THRESHOLD) {
            warnedAboutCascadingUpdates = true;
            console.warn(`${cascadingComponentUpdateCount} cascading component updates detected in one event loop turn. Consider reducing hydration regions. Components: ${formatComponentCounts(cascadingComponentNameCounts)}`);
        }
        if (componentUpdateCount > MAX_CASCADING_COMPONENT_UPDATES) {
            let error = new Error(`handle.update() infinite loop detected in ${componentName} after ${componentUpdateCount} cascading updates. Components: ${formatComponentCounts(cascadingComponentNameCounts)}`);
            dispatchError(error);
            return false;
        }
        return true;
    }
    function flush() {
        if (flushing)
            return;
        flushing = true;
        try {
            while (true) {
                flushScheduled = false;
                let batch = new Map(scheduled);
                scheduled.clear();
                let hasWork = batch.size > 0 ||
                    workTasks.length > 0 ||
                    commitPhaseTasks.length > 0 ||
                    postCommitTasks.length > 0;
                if (!hasWork)
                    return;
                documentState.capture();
                let updateParents = batch.size > 0 ? Array.from(new Set(batch.values())) : [];
                setActiveSchedulerUpdateParents(updateParents);
                dispatchPhaseEvent('beforeUpdate', updateParents);
                if (batch.size > 0) {
                    let vnodes = Array.from(batch);
                    let noScheduledAncestor = new Set();
                    for (let [vnode, domParent] of vnodes) {
                        if (ancestorIsScheduled(vnode, batch, noScheduledAncestor))
                            continue;
                        if (!trackCascadingUpdate(vnode))
                            return;
                        let curr = vnode._content;
                        // Calculate anchor at render time from current vdom position (never stale).
                        // Needed for fragment self-updates that add children - without this, new children
                        // would be appended after siblings. The keyed diff has placement logic, but unkeyed
                        // diff relies on anchor for correct positioning.
                        let anchor = findNextSiblingDomAnchor(vnode) || undefined;
                        try {
                            renderComponent(curr, vnode, domParent, vnode._context, anchor);
                        }
                        catch (error) {
                            dispatchError(error);
                        }
                    }
                }
                flushTaskQueue(workTasks);
                setActiveSchedulerUpdateParents(undefined);
                // Restore selection before commit-phase lifecycle work so mixins see
                // the final DOM state but still run before commit listeners and user tasks.
                documentState.restore();
                flushTaskQueue(commitPhaseTasks);
                dispatchPhaseEvent('commit', updateParents);
                flushTaskQueue(postCommitTasks);
            }
        }
        finally {
            setActiveSchedulerUpdateParents(undefined);
            flushing = false;
        }
    }
    function dispatchPhaseEvent(type, parents) {
        if (phaseListenerCounts[type] === 0)
            return;
        let event = Object.assign(new Event(type), { parents });
        phaseEvents.dispatchEvent(event);
    }
    function flushTaskQueue(queue) {
        while (queue.length > 0) {
            let task = queue.shift();
            if (!task)
                continue;
            try {
                task();
            }
            catch (error) {
                dispatchError(error);
            }
        }
    }
    function scheduleFlush() {
        if (flushScheduled || flushing)
            return;
        flushScheduled = true;
        queueMicrotask(flush);
    }
    function ancestorIsScheduled(vnode, batch, safe) {
        let path = [];
        let current = vnode._parent;
        while (current) {
            // Already verified this node has no scheduled ancestor above it
            if (safe.has(current)) {
                for (let node of path)
                    safe.add(node);
                return false;
            }
            path.push(current);
            if (isCommittedComponentNode(current) && batch.has(current)) {
                return true;
            }
            current = current.kind === 'root' ? undefined : current._parent;
        }
        // Reached root - mark entire path as safe for future lookups
        for (let node of path)
            safe.add(node);
        return false;
    }
    scheduler = {
        enqueue(vnode, domParent) {
            scheduled.set(vnode, domParent);
            scheduleFlush();
        },
        enqueueWork(newTasks) {
            workTasks.push(...newTasks);
            scheduleFlush();
        },
        enqueueCommitPhase(newTasks) {
            commitPhaseTasks.push(...newTasks);
            scheduleFlush();
        },
        enqueueTasks(newTasks) {
            postCommitTasks.push(...newTasks);
            scheduleFlush();
        },
        addEventListener(type, listener, options) {
            if (listener)
                phaseListenerCounts[type]++;
            phaseEvents.addEventListener(type, listener, options);
        },
        removeEventListener(type, listener, options) {
            if (listener)
                phaseListenerCounts[type] = Math.max(0, phaseListenerCounts[type] - 1);
            phaseEvents.removeEventListener(type, listener, options);
        },
        dequeue() {
            flush();
        },
    };
    return scheduler;
}
//# sourceMappingURL=scheduler.js.map